import * as PIXI from "pixi.js";
import { symbolData, COIN_SYMBOL_IDS, JACKPOT_SYMBOL_IDS, ROYAL_SYMBOL_ID, BLANK_SYMBOL_ID, isCoinSymbol, isRoyalSymbol } from "../../SymbolData";
import { Settings, GAME_LAYOUT } from "../../settings";
import { FEATURES_CONFIG } from "../../featuresConfig";
import { formatToCustomLocale } from "../../utilities";
import { soundManager } from "../../SoundManager";
import { SOUND_NAME } from "../../sounds";
import store from "../../State/Store/store";

/**
 * RoyalCollectSymbolController
 *
 * Per-symbol spin engine for the Royal Collect feature.
 * 1:1 port of ReelControllerV2 for N=1 visible symbol.
 *
 * ReelControllerV2 formula (N = visible symbol count):
 *   SPRITE_COUNT = N*2 + 2
 *   VISIBLE_SYMBOL_INDEX = 1 + N
 *   STOP_MOVE_DISTANCE = N
 *
 * For N=1: 4 sprites, visible at index 2, stop moves by 1 symbol height.
 * [0: top buffer, 1: landing zone, 2: VISIBLE, 3: bottom buffer]
 */

// Dynamic sprite count formula: 1 top buffer + N visible + N landing zone + 1 bottom buffer
const SPRITE_COUNT = 4; // 1*2 + 2
const VISIBLE_SYMBOL_INDEX = 2; // 1 + 1
// Stop animation moves sprites down by the landing zone distance (N symbol heights)
const STOP_MOVE_DISTANCE = 1;

// Coin value text Y offset derived from c.json spine's "value" bone (y=-82.67, skeleton h=338)
// In Spine Y-up coords, -82.67 means below center → in PixiJS Y-down, it's positive
const COIN_VALUE_Y_OFFSET = 82.67 / 338; // ≈ 0.2446 of symbolHeight

// Royal Collect base symbols — blanks only (the default non-coin/non-jackpot pool).
// During spin, coins are injected probabilistically via _generateRandomSymbolId().
const RC_BASE_SYMBOL_IDS = [BLANK_SYMBOL_ID].filter((id) => {
  const info = symbolData[id];
  return info?.texture && info.texture !== null;
});

// Coin pool for probability-based injection during spin
const RC_COIN_SYMBOL_IDS = COIN_SYMBOL_IDS.filter((id) => {
  const info = symbolData[id];
  return info?.texture && info.texture !== null;
});

// Jackpot pool for rare injection during spin (j1, j2, j3 — each with unique skin)
const RC_JACKPOT_SYMBOL_IDS = JACKPOT_SYMBOL_IDS.filter((id) => {
  const info = symbolData[id];
  return info?.texture && info.texture !== null;
});

// Probabilities during RC spin (~70% blanks, 25% coins, 5% jackpots)
const RC_COIN_PROBABILITY = 0.25;
const RC_JACKPOT_PROBABILITY = 0.05;
// Middle reel: royals + blanks only (20% royal, 80% blank)
const RC_ROYAL_PROBABILITY = 0.2;

class RoyalCollectSymbolController {
  constructor(
    spinningContainer,
    symbolId,
    symbolWidth,
    symbolHeight,
    symbolIndex,
    reelIndex,
    onStopCallback = null,
    symbolsSpriteSheet = null,
    symbolsSpriteSheetBlurred = null,
  ) {
    this.spinningContainer = spinningContainer;
    this.symbolId = symbolId;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.symbolIndex = symbolIndex;
    this.reelIndex = reelIndex;
    this.onStopCallback = onStopCallback;
    this.spritesheet = symbolsSpriteSheet;
    this.spritesheetBlurred = FEATURES_CONFIG.ROYAL_COLLECT.BLURRED_TEXTURES_ENABLED
      ? symbolsSpriteSheetBlurred
      : null;

    // Animation state
    this.isPlaying = false;
    this.hasStopped = false;
    this.isStopping = false;
    this.isWindingUp = false;
    this.tickerCallback = null;
    this._windUpTickerFn = null;
    this.spinningSprites = [];
    this.spinStartTime = null;
    this.normalizedSpinSpeed = 0;
    this.lastCalculatedPosition = 0;
    this.stopTweenActive = false;

    // Premium feel: acceleration + velocity-based blur
    this.accelerationMultiplier = 1.0;
    this.currentVelocity = 0;
    this.isBlurred = false;

    // Landing data from API
    this.pendingLandingSymbolId = null;
    // The royal symbol on the middle reel (index 1) is permanent — it never spins
    this.isPermanentRoyal = reelIndex === 1 && isRoyalSymbol(symbolId);
    // Set after collection — next spin starts with empty space instead of stale coin texture
    this.collectedBlank = false;

    // Ticker-based stop params (replaces anime.js)
    this._stopStartTime = null;
    this._stopDistance = 0;
    this._stopDuration = 0;
    this._stopOvershoot = 0;
    this._stopLastEasedPos = 0;

    // Speed mode — set per-spin by the context before calling startSpin()
    this.autoPlaySpeed = "auto";
    this._speedConfigCache = null;

    // Caches
    this._wrapThresholdCache = null;
    this._lastSymbolHeightForWrapThreshold = null;
    this._nativeDimensionsCache = new Map();

    // Only create spinning sprites for non-permanent symbols
    if (!this.isPermanentRoyal) {
      this._createSpinningSprites();
      this.spinningContainer.roundPixels = true;
    }
  }

  // ── Speed Config (mirrors ReelControllerV2._getSpeedConfig) ─────────────

  /**
   * Returns the speed-dependent config for the current autoPlaySpeed.
   * Reads from FEATURES_CONFIG.ROYAL_COLLECT.AUTO_PLAY_SPEEDS[AUTO|QUICK|TURBO].
   * Speed is set per-spin by the context before calling startSpin().
   */
  _getSpeedConfig() {
    if (this._speedConfigCache && this._speedConfigCache._speed === this.autoPlaySpeed) {
      return this._speedConfigCache;
    }

    const speeds = FEATURES_CONFIG.ROYAL_COLLECT.AUTO_PLAY_SPEEDS;
    const config = speeds[this.autoPlaySpeed?.toUpperCase()] || speeds.AUTO;

    // Tag cache with the speed it was built for
    config._speed = this.autoPlaySpeed;
    this._speedConfigCache = config;

    return config;
  }

  // ── Sprite Creation (from ReelControllerV2) ────────────────────────────

  _createSpinningSprites() {
    if (!this.spinningContainer) return;

    for (let i = 0; i < SPRITE_COUNT; i++) {
      const symbolContainer = new PIXI.Container();
      symbolContainer.y = i * this.symbolHeight;
      symbolContainer.roundPixels = true;

      const normalSprite = new PIXI.Sprite();
      normalSprite.anchor.set(0.5);
      normalSprite.name = "spinning_symbol_texture";
      normalSprite.alpha = 1;
      normalSprite.roundPixels = true;
      symbolContainer.addChild(normalSprite);

      const blurredSprite = new PIXI.Sprite();
      blurredSprite.anchor.set(0.5);
      blurredSprite.name = "spinning_symbol_texture_blurred";
      blurredSprite.alpha = 0;
      blurredSprite.roundPixels = true;
      symbolContainer.addChild(blurredSprite);

      let valueText = null;
      let valueTextBlurred = null;
      if (COIN_SYMBOL_IDS.length > 0) {
        const fontSize =
          this.symbolHeight * Settings.COIN_VALUE_FONT_SIZE_MULTIPLIER;
        valueText = new PIXI.BitmapText("", {
          fontName: "font_secondary_scaled",
          fontSize: fontSize,
        });
        valueText.anchor.set(0.5);
        valueText.name = "symbol_value";
        valueText.zIndex = 1;
        valueText.alpha = 0;
        valueText.roundPixels = true;
        symbolContainer.addChild(valueText);

        if (
          this.spritesheetBlurred &&
          PIXI.BitmapFont.available["font_secondary_blurred"]
        ) {
          valueTextBlurred = new PIXI.BitmapText("", {
            fontName: "font_secondary_blurred",
            fontSize: fontSize,
          });
          valueTextBlurred.anchor.set(0.5);
          valueTextBlurred.name = "symbol_value_blurred";
          valueTextBlurred.zIndex = 1;
          valueTextBlurred.alpha = 0;
          valueTextBlurred.roundPixels = true;
          symbolContainer.addChild(valueTextBlurred);
        }
      }

      this.spinningSprites.push({
        container: symbolContainer,
        normalSprite,
        blurredSprite,
        valueText,
        valueTextBlurred,
        currentSymbolId: null,
      });

      this.spinningContainer.addChild(symbolContainer);
    }

    // Initialize with random textures
    this._initializeSpinningTextures();

    // Start hidden — startSpin() will make them visible
    for (let i = 0; i < this.spinningSprites.length; i++) {
      this.spinningSprites[i].container.visible = false;
    }
  }

  _initializeSpinningTextures() {
    for (let i = 0; i < this.spinningSprites.length; i++) {
      const randomId = this._generateRandomSymbolId();
      this._setSpriteTexture(this.spinningSprites[i], randomId);
    }
  }

  // Picks a random base symbol (blanks — no coins)
  _pickRandomSymbolId() {
    if (!RC_BASE_SYMBOL_IDS.length) return BLANK_SYMBOL_ID;
    return RC_BASE_SYMBOL_IDS[
      Math.floor(Math.random() * RC_BASE_SYMBOL_IDS.length)
    ];
  }

  // Generates random symbol with probability-based coin/jackpot/royal injection.
  // Reels 0 & 2: ~70% blanks, ~25% coins, ~5% jackpots
  // Reel 1 (middle): ~80% blanks, ~20% royals (no coins/jackpots)
  _generateRandomSymbolId() {
    // Middle reel — exclusively royals + blanks
    if (this.reelIndex === GAME_LAYOUT.MIDDLE_REEL) {
      if (ROYAL_SYMBOL_ID && Math.random() < RC_ROYAL_PROBABILITY) {
        return ROYAL_SYMBOL_ID;
      }
      return this._pickRandomSymbolId();
    }

    // Side reels — coins + jackpots + blanks
    const roll = Math.random();

    // Jackpot symbols — rarest, check first
    if (
      RC_JACKPOT_SYMBOL_IDS.length > 0 &&
      roll < RC_JACKPOT_PROBABILITY
    ) {
      return RC_JACKPOT_SYMBOL_IDS[
        Math.floor(Math.random() * RC_JACKPOT_SYMBOL_IDS.length)
      ];
    }

    // Coin symbols — more common
    if (
      RC_COIN_SYMBOL_IDS.length > 0 &&
      roll < RC_JACKPOT_PROBABILITY + RC_COIN_PROBABILITY
    ) {
      return RC_COIN_SYMBOL_IDS[
        Math.floor(Math.random() * RC_COIN_SYMBOL_IDS.length)
      ];
    }

    // Default: blank symbol
    return this._pickRandomSymbolId();
  }

  // ── Coin Display Value (from ReelControllerV2) ─────────────────────────

  _getCoinDisplayValue(symbolValue) {
    if (!symbolValue || symbolValue === null) {
      return "";
    }

    try {
      const coinValue = parseInt(symbolValue, 10);

      if (isNaN(coinValue) || coinValue <= 0) {
        return "";
      }

      // Use free rounds bet when active, otherwise use regular bet from store
      const state = store.getState();
      const freeRoundsActive = state?.freeRounds?.status === "active";
      const freeRoundsBet = state?.freeRounds?.betValue || null;
      const bet =
        freeRoundsActive && freeRoundsBet ? freeRoundsBet : state.game.bet || 1;

      const displayValue = coinValue * bet;
      return formatToCustomLocale(displayValue);
    } catch (error) {
      return "";
    }
  }

  // ── Symbol Scale (from ReelControllerV2) ───────────────────────────────

  // Calculates uniform symbol scale based on standard symbol reference height.
  // All symbols get the SAME scale factor — premium symbols with larger sourceSize
  // naturally overflow their cells, preserving the intended size relationships.
  // Cached per container dimensions for performance (scale is symbol-independent).
  _calculateSymbolScale(containerWidth, containerHeight) {
    if (!containerHeight) return 1;

    // Check cache — uniform scale only depends on container dimensions
    const cacheKey = `uniform_${containerWidth}_${containerHeight}`;
    if (this._nativeDimensionsCache.has(cacheKey)) {
      return this._nativeDimensionsCache.get(cacheKey);
    }

    // Uniform scale: all symbols scale relative to the standard symbol height (168px).
    const scale = containerHeight / Settings.SYMBOL_HEIGHT;

    // Cache result
    this._nativeDimensionsCache.set(cacheKey, scale);

    return scale;
  }

  // ── Texture Management (from ReelControllerV2) ─────────────────────────

  // Sets sprite textures (normal/blurred) and coin value text
  _setSpriteTexture(spriteData, symbolId) {
    if (spriteData.currentSymbolId === symbolId) {
      return;
    }

    const symbolInfo = symbolData[symbolId];
    if (!symbolInfo) {
      spriteData.normalSprite.texture = PIXI.Texture.EMPTY;
      spriteData.normalSprite.alpha = 0.001;
      if (spriteData.blurredSprite) {
        spriteData.blurredSprite.texture = PIXI.Texture.EMPTY;
        spriteData.blurredSprite.alpha = 0.001;
      }
      if (spriteData.valueText) {
        spriteData.valueText.text = "";
        spriteData.valueText.alpha = 0;
      }
      spriteData.currentSymbolId = null;
      return;
    }


    spriteData.currentSymbolId = symbolId;
    const textureName = symbolInfo.texture;
    const symbolValue = symbolInfo.value;

    // If texture is null or empty, set empty textures and skip processing
    if (!textureName || textureName === null) {
      spriteData.normalSprite.texture = PIXI.Texture.EMPTY;
      spriteData.normalSprite.alpha = 0.001;
      if (spriteData.blurredSprite) {
        spriteData.blurredSprite.texture = PIXI.Texture.EMPTY;
        spriteData.blurredSprite.alpha = 0.001;
      }
      if (spriteData.valueText) {
        spriteData.valueText.text = "";
        spriteData.valueText.alpha = 0;
      }
      spriteData.currentSymbolId = null;
      return;
    }

    if (this.spritesheet && textureName) {
      const texture =
        this.spritesheet.textures?.[`${textureName}.png`] ||
        PIXI.Texture.EMPTY;
      spriteData.normalSprite.texture = texture;
      spriteData.normalSprite.alpha = this.isBlurred ? 0.001 : 1;

      if (this.spritesheetBlurred && spriteData.blurredSprite) {
        const blurredTexture =
          this.spritesheetBlurred.textures?.[`${textureName}_blurred.png`] ||
          PIXI.Texture.EMPTY;
        spriteData.blurredSprite.texture = blurredTexture;
        spriteData.blurredSprite.alpha = this.isBlurred ? 1 : 0.001;
      }
    }

    const scale = this._calculateSymbolScale(
      this.symbolWidth,
      this.symbolHeight,
    );
    if (spriteData.normalSprite) {
      spriteData.normalSprite.scale.set(scale);
    }
    if (spriteData.blurredSprite) {
      // Blurred textures have same canvas size as normal (squash is baked into content).
      // Use same uniform scale so blurred sprites match normal sprite size.
      spriteData.blurredSprite.scale.set(scale);
    }

    // Update coin value text — ONLY for coin symbols (not jackpots which have values but display via spine)
    // This avoids store.getState(), updateText(), regex, and scale calculations in the hot loop
    if (spriteData.valueText && symbolValue && isCoinSymbol(symbolId)) {
      const displayValue = this._getCoinDisplayValue(symbolValue);
      spriteData.valueText.text = displayValue;
      spriteData.valueText.alpha =
        displayValue !== "" ? (this.isBlurred ? 0 : 1) : 0;
      spriteData.valueText.y = this.symbolHeight * COIN_VALUE_Y_OFFSET;

      // Apply scaling logic if text has content
      if (displayValue !== "") {
        // Reset scale first
        spriteData.valueText.scale.set(1);

        // Force update to get accurate measurements
        spriteData.valueText.updateText();

        // Calculate available width (with padding)
        const coinWidth = this.symbolWidth;
        const paddingPercent = 0.12; // 12% padding on each side
        const maxTextWidth = coinWidth * (1 - paddingPercent * 2);

        const actualTextWidth = spriteData.valueText.width;

        // If text is too wide, scale down uniformly to fit (preserves aspect ratio)
        if (actualTextWidth > maxTextWidth) {
          const textScale = maxTextWidth / actualTextWidth;
          spriteData.valueText.scale.set(textScale);
        }
      }

      // Update blurred text with same value and scaling
      if (spriteData.valueTextBlurred) {
        spriteData.valueTextBlurred.text = displayValue;
        spriteData.valueTextBlurred.alpha =
          displayValue !== "" ? (this.isBlurred ? 1 : 0) : 0;
        spriteData.valueTextBlurred.y =
          this.symbolHeight * COIN_VALUE_Y_OFFSET;

        if (displayValue !== "") {
          spriteData.valueTextBlurred.scale.set(1);
          spriteData.valueTextBlurred.updateText();

          const coinWidth = this.symbolWidth;
          const paddingPercent = 0.1;
          const maxTextWidth = coinWidth * (1 - paddingPercent * 2);

          const actualTextWidth = spriteData.valueTextBlurred.width;

          // If text is too wide, scale down uniformly to fit (preserves aspect ratio)
          if (actualTextWidth > maxTextWidth) {
            const textScale = maxTextWidth / actualTextWidth;
            spriteData.valueTextBlurred.scale.set(textScale);
          }
        }
      }
    } else if (spriteData.valueText) {
      // Non-coin symbol: clear text without heavy processing
      spriteData.valueText.text = "";
      spriteData.valueText.alpha = 0;
      if (spriteData.valueTextBlurred) {
        spriteData.valueTextBlurred.text = "";
        spriteData.valueTextBlurred.alpha = 0;
      }
    }
  }

  // ── Start Spin ────────────────────────────────────────────────────────

  startSpin() {
    if (this.isPermanentRoyal || this.isPlaying) return;

    this.isPlaying = true;
    this.hasStopped = false;
    this.isStopping = false;
    this.stopTweenActive = false;
    this.accelerationMultiplier = 0; // Ramp from 0
    this.currentVelocity = 0;
    this.isBlurred = false;
    // pendingLandingSymbolId is cleared by handleSpin() before startSpin() is
    // called. New API data arrives via setLandingSymbolId() after rgsPlay().

    // Reset stop params
    this._stopStartTime = null;
    this._stopDistance = 0;
    this._stopDuration = 0;
    this._stopOvershoot = 0;
    this._stopLastEasedPos = 0;

    // Calculate spin speed from speed-dependent config
    const speedConfig = this._getSpeedConfig();
    this.normalizedSpinSpeed =
      (this.symbolHeight / speedConfig.SPEED_MS_PER_SYMBOL_HEIGHT) * 1000;
    this.spinStartTime = performance.now();
    this.lastCalculatedPosition = 0;

    // Reset sprite positions
    for (let i = 0; i < this.spinningSprites.length; i++) {
      this.spinningSprites[i].container.y = i * this.symbolHeight;
    }

    // Make spinning sprites visible (they start hidden)
    for (let i = 0; i < this.spinningSprites.length; i++) {
      this.spinningSprites[i].container.visible = true;
    }

    // Set visible sprite (index 2) to current symbol so it visually pushes down,
    // other sprites get random textures for the spinning animation
    for (let i = 0; i < this.spinningSprites.length; i++) {
      if (i === VISIBLE_SYMBOL_INDEX) {
        if (this.collectedBlank) {
          // Collected position — start with empty space (no stale coin texture)
          // CRITICAL: Reset currentSymbolId so _setSpriteTexture doesn't bail
          // on a stale match when this sprite wraps and needs a new texture.
          this.spinningSprites[i].currentSymbolId = null;
          this.spinningSprites[i].normalSprite.texture = PIXI.Texture.EMPTY;
          this.spinningSprites[i].normalSprite.alpha = 0.001;
          if (this.spinningSprites[i].blurredSprite) {
            this.spinningSprites[i].blurredSprite.texture = PIXI.Texture.EMPTY;
            this.spinningSprites[i].blurredSprite.alpha = 0;
          }
          if (this.spinningSprites[i].valueText) {
            this.spinningSprites[i].valueText.text = '';
            this.spinningSprites[i].valueText.alpha = 0;
          }
          this.collectedBlank = false;
        } else {
          this._setSpriteTexture(this.spinningSprites[i], this.symbolId);
        }
      } else {
        const randomId = this._generateRandomSymbolId();
        this._setSpriteTexture(this.spinningSprites[i], randomId);
      }
    }

    // Show all spinning sprites with normal textures
    this._switchToNormalTextures();

    // Start ticker loop
    this.tickerCallback = () => {
      if (this.spinStartTime === null || this.hasStopped) return;
      this.updateSymbolsAbsolute();
    };
    PIXI.Ticker.shared.add(this.tickerCallback);
  }

  // ── Start Spin with Wind-Up (AUTO speed only) ──────────────────────

  /**
   * Start spin with a wind-up animation: symbol pulls upward, then releases
   * into the spin. Wind-up distance = landing bounce overshoot distance.
   *
   * Uses ticker-based sprite position offsets (not container.y) to avoid
   * React re-render conflicts that broke the previous implementation.
   *
   * @returns {Promise<void>} Resolves when wind-up completes and spin starts
   */
  startSpinWithWindUp() {
    if (this.isPermanentRoyal || this.isPlaying || this.isWindingUp) {
      return Promise.resolve();
    }

    this.isWindingUp = true;

    // ── Sprite setup (same as startSpin) ──────────────────────────────
    this.hasStopped = false;
    this.isStopping = false;
    this.stopTweenActive = false;
    this.accelerationMultiplier = 0;
    this.currentVelocity = 0;
    this.isBlurred = false;
    // pendingLandingSymbolId is cleared by handleSpin() before startSpinWithWindUp()
    // is called. New API data arrives via setLandingSymbolId() after rgsPlay().

    this._stopStartTime = null;
    this._stopDistance = 0;
    this._stopDuration = 0;
    this._stopOvershoot = 0;
    this._stopLastEasedPos = 0;

    const speedConfig = this._getSpeedConfig();
    this.normalizedSpinSpeed =
      (this.symbolHeight / speedConfig.SPEED_MS_PER_SYMBOL_HEIGHT) * 1000;

    // Reset sprite positions and make visible
    for (let i = 0; i < this.spinningSprites.length; i++) {
      this.spinningSprites[i].container.y = i * this.symbolHeight;
      this.spinningSprites[i].container.visible = true;
    }

    // Set visible sprite to current symbol, others get random textures
    for (let i = 0; i < this.spinningSprites.length; i++) {
      if (i === VISIBLE_SYMBOL_INDEX) {
        if (this.collectedBlank) {
          // Collected position — start with empty space (no stale coin texture)
          // CRITICAL: Reset currentSymbolId so _setSpriteTexture doesn't bail
          // on a stale match when this sprite wraps and needs a new texture.
          this.spinningSprites[i].currentSymbolId = null;
          this.spinningSprites[i].normalSprite.texture = PIXI.Texture.EMPTY;
          this.spinningSprites[i].normalSprite.alpha = 0.001;
          if (this.spinningSprites[i].blurredSprite) {
            this.spinningSprites[i].blurredSprite.texture = PIXI.Texture.EMPTY;
            this.spinningSprites[i].blurredSprite.alpha = 0;
          }
          if (this.spinningSprites[i].valueText) {
            this.spinningSprites[i].valueText.text = '';
            this.spinningSprites[i].valueText.alpha = 0;
          }
          this.collectedBlank = false;
        } else {
          this._setSpriteTexture(this.spinningSprites[i], this.symbolId);
        }
      } else {
        const randomId = this._generateRandomSymbolId();
        this._setSpriteTexture(this.spinningSprites[i], randomId);
      }
    }
    this._switchToNormalTextures();

    // ── Wind-up distance ──────────────────────────────────────────────
    // Simple fraction of symbolHeight — tunable via config (WIND_UP_DISTANCE)
    // Negative = pull upward
    const windUpDistance = -(speedConfig.WIND_UP_DISTANCE || 0.15) * this.symbolHeight;

    // ── Wind-up timing ───────────────────────────────────────────────
    // Derive from stop timing to match bounce feel (same as base game)
    const s = speedConfig.BOUNCE_OVERSHOOT;
    const spinVelocity =
      (this.symbolHeight / speedConfig.SPEED_MS_PER_SYMBOL_HEIGHT) * 1000;
    const totalStopDuration =
      (((s + 3) * STOP_MOVE_DISTANCE * this.symbolHeight * 1000) /
        spinVelocity) *
      (FEATURES_CONFIG.ROYAL_COLLECT.STOP_SLOWDOWN_FACTOR || 1.0);
    const bounceFraction = (2 * s) / (3 * (s + 1));
    const windUpPullDuration = Math.round(totalStopDuration * bounceFraction) || 55;
    const windUpReleaseDuration = Math.round(windUpPullDuration * 0.6);

    // ── Ticker-based wind-up animation ───────────────────────────────
    return new Promise((resolve) => {
      let elapsed = 0;

      const windUpTicker = () => {
        elapsed += PIXI.Ticker.shared.deltaMS;

        let offset;
        if (elapsed < windUpPullDuration) {
          // Phase 1: Pull up (easeOutCubic — fast start, gentle peak)
          const t = Math.min(elapsed / windUpPullDuration, 1);
          const eased = 1 - (1 - t) * (1 - t) * (1 - t);
          offset = windUpDistance * eased;
        } else {
          // Phase 2: Release back to origin (easeInCubic — accelerates into spin)
          const releaseElapsed = elapsed - windUpPullDuration;
          const t = Math.min(releaseElapsed / windUpReleaseDuration, 1);
          const eased = t * t * t;
          offset = windUpDistance * (1 - eased);
        }

        // Apply offset to ALL sprite positions (not container.y)
        for (let i = 0; i < this.spinningSprites.length; i++) {
          this.spinningSprites[i].container.y = i * this.symbolHeight + offset;
        }

        // Check completion
        if (elapsed >= windUpPullDuration + windUpReleaseDuration) {
          PIXI.Ticker.shared.remove(windUpTicker);
          this._windUpTickerFn = null;

          // Reset to exact grid positions
          for (let i = 0; i < this.spinningSprites.length; i++) {
            this.spinningSprites[i].container.y = i * this.symbolHeight;
          }

          this.isWindingUp = false;

          // Start actual spin (ticker loop) — same as end of startSpin()
          this.isPlaying = true;
          this.spinStartTime = performance.now();
          this.lastCalculatedPosition = 0;

          this.tickerCallback = () => {
            if (this.spinStartTime === null || this.hasStopped) return;
            this.updateSymbolsAbsolute();
          };
          PIXI.Ticker.shared.add(this.tickerCallback);

          resolve();
        }
      };

      this._windUpTickerFn = windUpTicker;
      PIXI.Ticker.shared.add(windUpTicker);
    });
  }

  // ── Stop Spin ─────────────────────────────────────────────────────────

  stopSpin() {
    if (this.isStopping || this.hasStopped) return;
    // Allow stop during spinning OR wind-up.
    // During wind-up: isStopping flag is set now, and when wind-up completes
    // and the spin ticker starts, it'll execute the stop on the first wrap.
    if (!this.isPlaying && !this.isWindingUp) return;
    this.isStopping = true;
  }

  /**
   * Set the symbol ID that should appear when this symbol stops.
   * Called by the component when API response arrives.
   */
  setLandingSymbolId(symbolId) {
    this.pendingLandingSymbolId = symbolId;
  }

  // ── Main Animation Loop (from ReelControllerV2) ───────────────────────

  updateSymbolsAbsolute() {
    if (this.spinStartTime === null || this.hasStopped) return;

    // ── Ticker-based stop animation (BackOut easing inside same ticker) ──
    if (this.stopTweenActive && this._stopStartTime !== null) {
      const now = performance.now();
      const elapsed = now - this._stopStartTime;
      const t = Math.min(elapsed / this._stopDuration, 1.0);

      // BackOut easing: f(t) = (t-1)² × ((s+1)(t-1) + s) + 1
      const s = this._stopOvershoot;
      const t1 = t - 1;
      const easedT = t1 * t1 * ((s + 1) * t1 + s) + 1;
      const easedPosition = this._stopDistance * easedT;
      const delta = easedPosition - this._stopLastEasedPos;
      this._stopLastEasedPos = easedPosition;

      // Update blur from easing velocity
      // BackOut derivative: f'(t) = 3(s+1)(t-1)² + 2s(t-1)
      const easingVelocity = 3 * (s + 1) * t1 * t1 + 2 * s * t1;
      this.currentVelocity = Math.abs(easingVelocity) * 0.3;
      this._updateBlurFromVelocity();

      if (Math.abs(delta) > 0.0001) {
        for (let i = 0; i < this.spinningSprites.length; i++) {
          this.spinningSprites[i].container.y += delta;
        }

        // Wrap check during stop
        const wrapThreshold = this._getWrapThreshold();
        for (let i = 0; i < this.spinningSprites.length; i++) {
          if (this.spinningSprites[i].container.y >= wrapThreshold) {
            this.spinningSprites[i].container.y -= wrapThreshold;
          }
        }
      }

      if (t >= 1.0) {
        this._finalizeLanding();
      }
      return;
    }

    // ── Normal spin loop ──
    const now = performance.now();
    const elapsedTime = (now - this.spinStartTime) / 1000;
    const currentExpectedPosition = elapsedTime * this.normalizedSpinSpeed;

    let movement = currentExpectedPosition - this.lastCalculatedPosition;
    this.lastCalculatedPosition = currentExpectedPosition;

    // Acceleration ramp (ease-out cubic: fast start, smooth arrival at full speed)
    const speedConfig = this._getSpeedConfig();
    const accelDuration = speedConfig.ACCELERATION_DURATION || 0;
    if (accelDuration > 0 && this.accelerationMultiplier < 1.0) {
      const elapsedMs = now - this.spinStartTime;
      const accelT = Math.min(elapsedMs / accelDuration, 1.0);
      // Ease-out cubic: starts fast, smoothly reaches 1.0
      this.accelerationMultiplier =
        1 - (1 - accelT) * (1 - accelT) * (1 - accelT);
    } else {
      this.accelerationMultiplier = 1.0;
    }

    // Apply acceleration multiplier
    movement *= this.accelerationMultiplier;

    // Blur: smooth per-frame velocity-based crossfade
    this.currentVelocity = this.accelerationMultiplier;
    this._updateBlurFromVelocity();

    const wrapThreshold = this._getWrapThreshold();
    const shouldUpdateTexture = !this.isStopping && !this.hasStopped;

    let shouldExecuteStop = false;

    // Phase 1: Apply movement to ALL sprites uniformly (no break-and-snap)
    for (let i = 0; i < this.spinningSprites.length; i++) {
      this.spinningSprites[i].container.y += movement;
    }

    // Phase 2: Check for wraps AFTER all sprites have moved
    // Check the last sprite (highest Y) — at normal speeds only one wraps per frame
    const lastIndex = this.spinningSprites.length - 1;
    if (this.spinningSprites[lastIndex].container.y >= wrapThreshold) {
      this._moveLastSymbolOnTop();

      if (shouldUpdateTexture) {
        // Update texture on the sprite that just wrapped to top (now index 0)
        const randomId = this._generateRandomSymbolId();
        this._setSpriteTexture(this.spinningSprites[0], randomId);
      }

      if (this.isStopping && !this.hasStopped && !this.stopTweenActive) {
        // Only stop when we have landing data from the API
        // (prevents landing on a random symbol if stop is triggered before API response)
        if (this.pendingLandingSymbolId !== null) {
          shouldExecuteStop = true;
        }
      }
    }

    if (shouldExecuteStop) {
      this._calculateAndExecuteStop();
    }
  }

  // ── Wrap (from ReelControllerV2) ──────────────────────────────────────

  // Wraps last sprite to top — single-sprite teleport (no grid-snap on other sprites)
  _moveLastSymbolOnTop() {
    const movedSymbol = this.spinningSprites.pop();
    this.spinningSprites.unshift(movedSymbol);

    // Teleport only the wrapped sprite to the top position.
    // Other sprites keep their exact Y positions — no grid-snap stutter.
    const totalStripHeight = SPRITE_COUNT * this.symbolHeight;
    movedSymbol.container.y -= totalStripHeight;
  }

  _getWrapThreshold() {
    if (
      this._wrapThresholdCache === null ||
      this._lastSymbolHeightForWrapThreshold !== this.symbolHeight
    ) {
      this._wrapThresholdCache = SPRITE_COUNT * this.symbolHeight;
      this._lastSymbolHeightForWrapThreshold = this.symbolHeight;
    }
    return this._wrapThresholdCache;
  }

  // ── Stop Calculation (from ReelControllerV2) ──────────────────────────

  _calculateAndExecuteStop() {
    if (this.hasStopped || this.stopTweenActive) return;
    this.stopTweenActive = true;

    // Set landing texture on the landing zone sprite
    this._setLandingTexture();

    // Calculate drift compensation
    const currentDrift = this._calculateCurrentDrift();
    const baseLandingDistance = STOP_MOVE_DISTANCE * this.symbolHeight;
    const adjustedLandingDistance = baseLandingDistance - currentDrift;

    const speedConfig = this._getSpeedConfig();
    const overshoot = speedConfig.BOUNCE_OVERSHOOT;

    // Calculate stop duration — use LANDING_DURATION (fixed) when available,
    // otherwise fall back to velocity-matched calc (same as ReelControllerV2._calculateAndExecuteStop)
    let stopDuration;
    const landingDuration = speedConfig.LANDING_DURATION || null;
    if (landingDuration) {
      stopDuration = landingDuration;
    } else {
      const currentSpinVelocity = this.normalizedSpinSpeed;
      const config = FEATURES_CONFIG.ROYAL_COLLECT;
      const velocityMatchedDuration =
        ((overshoot + 3) * adjustedLandingDistance * 1000) / currentSpinVelocity;
      stopDuration = velocityMatchedDuration * config.STOP_SLOWDOWN_FACTOR;
    }

    // Landing symbols are above the visible area — set them to normal (non-blurred)
    // textures immediately so they're already sharp when they scroll into view
    {
      const landingZoneEnd = 1 + 1; // sprite[0] and sprite[1]
      for (let i = 0; i < landingZoneEnd; i++) {
        const spriteData = this.spinningSprites[i];
        if (!spriteData) continue;
        spriteData.normalSprite.alpha = 1;
        if (spriteData.blurredSprite) spriteData.blurredSprite.alpha = 0;
        if (spriteData.valueText && spriteData.valueText.text !== "") {
          spriteData.valueText.alpha = 1;
        }
        if (spriteData.valueTextBlurred) {
          spriteData.valueTextBlurred.alpha = 0;
        }
      }

      this.isBlurred = false;
    }

    // Store stop params for the ticker
    this._stopStartTime = performance.now();
    this._stopDistance = adjustedLandingDistance;
    this._stopDuration = stopDuration;
    this._stopOvershoot = overshoot;
    this._stopLastEasedPos = 0;

    this.isPlaying = false;
  }

  _setLandingTexture() {
    const landingId =
      this.pendingLandingSymbolId !== null
        ? this.pendingLandingSymbolId
        : this._pickRandomSymbolId();

    // Set on sprite[1] only (the landing zone, above visible)
    // sprite[0] is the top buffer — never visible, no need to set landing texture
    if (this.spinningSprites.length >= 2) {
      this._setSpriteTexture(this.spinningSprites[1], landingId);
    }

    // Track the symbol that will land
    this.symbolId = landingId;
  }

  _calculateCurrentDrift() {
    if (!this.spinningSprites.length) return 0;
    const expectedY = VISIBLE_SYMBOL_INDEX * this.symbolHeight;
    const actualY = this.spinningSprites[VISIBLE_SYMBOL_INDEX].container.y;
    return actualY - expectedY;
  }

  // ── Finalize Landing (from ReelControllerV2) ──────────────────────────

  _finalizeLanding() {
    // Remove ticker
    if (this.tickerCallback) {
      PIXI.Ticker.shared.remove(this.tickerCallback);
      this.tickerCallback = null;
    }

    // Clear stop params
    this._stopStartTime = null;
    this._stopDistance = 0;
    this._stopDuration = 0;
    this._stopOvershoot = 0;
    this._stopLastEasedPos = 0;

    // Rotate array: move last STOP_MOVE_DISTANCE sprites to front (single O(n) operation)
    const cut = this.spinningSprites.length - STOP_MOVE_DISTANCE;
    const tail = this.spinningSprites.splice(cut);
    this.spinningSprites.unshift(...tail);

    // Reset positions to grid
    for (let i = 0; i < this.spinningSprites.length; i++) {
      this.spinningSprites[i].container.y = i * this.symbolHeight;
    }

    // Set critical state flags immediately
    this.hasStopped = true;
    this.isStopping = false;
    this.stopTweenActive = false;
    this.currentVelocity = 0;
    this.spinStartTime = null;

    // Unblur all remaining sprites
    this._switchToNormalTextures();

    // Hide spinning sprites — the component will show the static sprite instead
    for (let i = 0; i < this.spinningSprites.length; i++) {
      this.spinningSprites[i].container.visible = false;
    }

    // Update isPermanentRoyal based on what actually landed —
    // the initial value was set at construction from the starting symbol,
    // but a spin can land a royal on the middle reel dynamically.
    if (this.reelIndex === 1 && isRoyalSymbol(this.symbolId)) {
      this.isPermanentRoyal = true;
    }

    // Play stop sound
    soundManager.play(SOUND_NAME.reel_stop);

    // Fire stop callback to the control context
    if (this.onStopCallback) {
      this.onStopCallback(this.symbolIndex, this.reelIndex);
    }
  }

  // ── Blur System (proportional crossfade from ReelControllerV2) ─────────

  // Proportional blur: sets alpha of normal/blurred sprites based on velocity
  // Below threshold = fully sharp, above = gradually blurred proportional to speed
  _updateBlurFromVelocity() {
    if (!this.spritesheetBlurred) return;

    const threshold = FEATURES_CONFIG.ROYAL_COLLECT.BLUR_VELOCITY_THRESHOLD || 0.6;
    const velocity = this.currentVelocity;

    // Calculate blur amount: 0 = fully sharp, 1 = fully blurred
    // Ramp from threshold to 1.0 (full speed), squared for natural feel
    let blurAmount;
    if (velocity < threshold) {
      blurAmount = 0;
    } else {
      // Normalized 0-1 within the blur range, squared for natural feel
      const t = (velocity - threshold) / (1.0 - threshold);
      blurAmount = Math.min(t * t, 1.0);
    }

    // Update isBlurred state for cleanup/state tracking
    this.isBlurred = blurAmount > 0.01;

    // Apply alpha proportionally to all sprites
    const normalAlpha = 1 - blurAmount;
    const blurredAlpha = blurAmount;

    for (let i = 0; i < this.spinningSprites.length; i++) {
      const spriteData = this.spinningSprites[i];
      if (!spriteData || !spriteData.blurredSprite) continue;

      // During stop animation, skip landing zone + buffer sprites (already unblurred)
      if (this.stopTweenActive) {
        const landingZoneEnd = 1 + 1; // sprite[0] and sprite[1]
        if (i >= 0 && i < landingZoneEnd) continue;
      }

      spriteData.normalSprite.alpha = Math.max(normalAlpha, 0.001);
      spriteData.blurredSprite.alpha = blurredAlpha;

      // Blend coin value text alpha too
      if (spriteData.valueText && spriteData.valueText.text !== "") {
        spriteData.valueText.alpha = normalAlpha;
      }
      if (
        spriteData.valueTextBlurred &&
        spriteData.valueTextBlurred.text !== ""
      ) {
        spriteData.valueTextBlurred.alpha = blurredAlpha;
      }
    }
  }

  // Unblur all sprites — instant transition
  _switchToNormalTextures() {
    this.spinningSprites.forEach((sprite) => {
      if (!sprite || !sprite.blurredSprite) return;

      // Don't force alpha=1 on sprites with EMPTY texture (collectedBlank path).
      // The collectedBlank logic in startSpin/startSpinWithWindUp sets the visible
      // sprite to EMPTY + alpha 0.001 so collected positions stay invisible during spin.
      // Overriding that alpha would make the empty sprite "visible" (harmless visually
      // since EMPTY renders nothing, but incorrect state that can confuse later logic).
      if (sprite.normalSprite.texture !== PIXI.Texture.EMPTY) {
        sprite.normalSprite.alpha = 1;
      }
      sprite.blurredSprite.alpha = 0;

      // Switch text back to normal
      if (sprite.valueText && sprite.valueText.text !== "") {
        sprite.valueText.alpha = 1;
      }
      if (sprite.valueTextBlurred) {
        sprite.valueTextBlurred.alpha = 0;
      }
    });

    this.isBlurred = false;
  }

  // ── Resize ────────────────────────────────────────────────────────────

  resize(symbolWidth, symbolHeight) {
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this._nativeDimensionsCache.clear();
    this._wrapThresholdCache = null;
    this._lastSymbolHeightForWrapThreshold = null;

    // Update sprite positions (only when not animating)
    if (!this.isPlaying && !this.stopTweenActive) {
      for (let i = 0; i < this.spinningSprites.length; i++) {
        this.spinningSprites[i].container.y = i * this.symbolHeight;
      }
    }

    // Rescale all sprite textures using _setSpriteTexture's internal scaling
    for (let i = 0; i < this.spinningSprites.length; i++) {
      const spriteData = this.spinningSprites[i];
      if (spriteData.currentSymbolId !== null) {
        // Force re-set by clearing current ID
        const currentId = spriteData.currentSymbolId;
        spriteData.currentSymbolId = null;
        this._setSpriteTexture(spriteData, currentId);
      }
    }

    // Recalculate spin speed if currently spinning
    if (this.isPlaying) {
      const speedConfig = this._getSpeedConfig();
      this.normalizedSpinSpeed =
        (this.symbolHeight / speedConfig.SPEED_MS_PER_SYMBOL_HEIGHT) * 1000;
    }
  }

  // ── Cleanup ───────────────────────────────────────────────────────────

  cleanup() {
    if (this._windUpTickerFn) {
      PIXI.Ticker.shared.remove(this._windUpTickerFn);
      this._windUpTickerFn = null;
      this.isWindingUp = false;
    }
    if (this.tickerCallback) {
      PIXI.Ticker.shared.remove(this.tickerCallback);
      this.tickerCallback = null;
    }

    for (let i = 0; i < this.spinningSprites.length; i++) {
      const spriteData = this.spinningSprites[i];
      if (spriteData.container.parent) {
        spriteData.container.parent.removeChild(spriteData.container);
      }
      spriteData.container.destroy({ children: true });
    }
    this.spinningSprites = [];
    this._nativeDimensionsCache.clear();
  }
}

export default RoyalCollectSymbolController;
